import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/axios';
import { useToast } from '../hooks/use-toast';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from '../components/ui/tabs';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from '../components/ui/dialog';
import { 
  Shield, 
  CheckCircle, 
  IndianRupee, 
  Search, 
  Users, 
  Wallet, 
  Loader2, 
  Eye,
  Check,
  X,
  Clock,
  Edit,
  Save
} from 'lucide-react';

export default function AdminDashboard() {
  const { toast } = useToast();
  
  // -- Data State --
  const [users, setUsers] = useState([]);
  const [pendingTxs, setPendingTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // -- Modals State --
  const [selectedUserForTopup, setSelectedUserForTopup] = useState(null);
  const [topupAmount, setTopupAmount] = useState('');
  const [viewingProof, setViewingProof] = useState(null);
  
  // -- Edit User State --
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // 1. Fetching Engine
  const fetchAdminData = useCallback(async () => {
    try {
      const [usersRes, txRes] = await Promise.all([
        api.get('/admin/users'),
        api.get('/billing/transactions?status=pending')
      ]);

      if (usersRes.data.success) setUsers(usersRes.data.data || []);
      if (txRes.data.success) {
        const filtered = (txRes.data.transactions || txRes.data.data || [])
          .filter(t => t.status === 'pending');
        setPendingTxs(filtered);
      }
    } catch (error) {
      toast({ title: 'Access Denied', description: 'Admin privileges required.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchAdminData();
  }, [fetchAdminData]);

  // 2. Financial: Direct Top-up
  const handleDirectTopup = async (e) => {
    e.preventDefault();
    if (!selectedUserForTopup || !topupAmount) return;

    try {
      const { data } = await api.post('/admin/billing/topup', {
        userId: selectedUserForTopup._id,
        amount: parseFloat(topupAmount),
        description: 'Direct Admin Credit Injection'
      });
      if (data.success) {
        toast({ title: 'Success', description: `Injected ₹${topupAmount} into wallet.` });
        setTopupAmount('');
        setSelectedUserForTopup(null);
        fetchAdminData();
      }
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.error || 'Injection failed.', variant: 'destructive' });
    }
  };

  // 3. Financial: Payment Approval Logic
  const handleProcessPayment = async (txId, action) => {
    try {
      const endpoint = action === 'approve' 
        ? `/admin/billing/approve/${txId}` 
        : `/admin/billing/reject/${txId}`;
      
      const { data } = await api.post(endpoint);
      
      if (data.success) {
        toast({ title: action === 'approve' ? 'Payment Verified' : 'Payment Rejected' });
        setViewingProof(null);
        fetchAdminData();
      }
    } catch (error) {
      toast({ title: 'Processing Error', description: error.response?.data?.error || 'Failed to update transaction.', variant: 'destructive' });
    }
  };

  // 4. Governance: Suspend / Unsuspend User
  const toggleSuspend = async (user) => {
    try {
      const { data } = await api.put(`/admin/users/${user._id}/suspend`, {
        suspend: !user.is_suspended
      });
      if (data.success) {
        toast({ title: 'Status Updated', description: data.message });
        fetchAdminData();
      }
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.error || 'Failed to update user status.', variant: 'destructive' });
    }
  };

  // 5. Governance: Edit User Profile
  const openEditModal = (user) => {
    setEditingUser(user);
    // Populate form with existing data, using nullish coalescing (??) for numbers that could be 0
    setEditForm({
      username: user.username || '',
      email: user.email || '',
      role: user.role || 'user',
      ai_bot_enabled: user.ai_bot_enabled ? 'true' : 'false',
      cost_per_text_msg: user.cost_per_text_msg ?? 0.01,
      cost_per_image_msg: user.cost_per_image_msg ?? 0.05,
      cost_per_ai_reply: user.cost_per_ai_reply ?? 0.02,
      max_msgs_per_day: user.max_msgs_per_day || 500,
      max_ai_tokens_per_day: user.max_ai_tokens_per_day || 50000,
      max_image_size_mb: user.max_image_size_mb || 5.0,
    });
  };

  const handleEditChange = (e) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      // Safely parse strings back to required DB types
      const payload = {
        ...editForm,
        ai_bot_enabled: editForm.ai_bot_enabled === 'true',
        max_msgs_per_day: parseInt(editForm.max_msgs_per_day, 10),
        max_ai_tokens_per_day: parseInt(editForm.max_ai_tokens_per_day, 10),
        cost_per_text_msg: parseFloat(editForm.cost_per_text_msg),
        cost_per_image_msg: parseFloat(editForm.cost_per_image_msg),
        cost_per_ai_reply: parseFloat(editForm.cost_per_ai_reply),
        max_image_size_mb: parseFloat(editForm.max_image_size_mb),
      };

      const { data } = await api.put(`/admin/users/${editingUser._id}`, payload);
      
      if (data.success) {
        toast({ title: 'Profile Updated', description: 'User settings saved successfully.' });
        setEditingUser(null);
        fetchAdminData();
      }
    } catch (error) {
      toast({ 
        title: 'Update Failed', 
        description: error.response?.data?.error || 'Failed to save changes.', 
        variant: 'destructive' 
      });
    } finally {
      setIsSaving(false);
    }
  };

  // -- Computations --
  const totalLiquidity = users.reduce((acc, curr) => acc + (Number(curr.wallet_balance) || 0), 0);
  const filteredUsers = users.filter(u => 
    u.username?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return (
    <div className="h-screen flex flex-col items-center justify-center bg-slate-50 gap-4">
      <Loader2 className="w-12 h-12 animate-spin text-indigo-600" />
      <p className="font-bold text-slate-500">Syncing Master Database...</p>
    </div>
  );

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8 animate-in fade-in duration-500">
      
      {/* STATS HEADER */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-indigo-600 rounded-2xl shadow-xl shadow-indigo-100">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-black text-slate-900 tracking-tight">System Admin</h1>
            <p className="text-slate-500 font-medium">Platform governance and financial verification.</p>
          </div>
        </div>

        <div className="flex gap-4">
          <Card className="px-6 py-3 border-none shadow-sm ring-1 ring-slate-200">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Users</p>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-indigo-600" />
              <span className="text-2xl font-black">{users.length}</span>
            </div>
          </Card>
          <Card className="px-6 py-3 border-none shadow-sm bg-emerald-50 ring-1 ring-emerald-100">
            <p className="text-[10px] font-black text-emerald-400 uppercase tracking-widest mb-1">Total Liquidity</p>
            <div className="flex items-center gap-2 text-emerald-700">
              <Wallet className="w-4 h-4" />
              <span className="text-2xl font-black">₹{totalLiquidity.toLocaleString()}</span>
            </div>
          </Card>
        </div>
      </div>

      <Tabs defaultValue="users" className="space-y-6">
        <TabsList className="bg-slate-100 p-1 rounded-xl h-12">
          <TabsTrigger value="users" className="rounded-lg px-6 font-bold">User Directory</TabsTrigger>
          <TabsTrigger value="payments" className="rounded-lg px-6 font-bold flex gap-2">
            Verification Requests
            {pendingTxs.length > 0 && (
              <Badge className="bg-rose-500 text-white border-none h-5 w-5 p-0 flex items-center justify-center text-[10px]">
                {pendingTxs.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* TAB 1: USERS DIRECTORY */}
        <TabsContent value="users" className="space-y-6">
          <Card className="border-none shadow-xl ring-1 ring-slate-200 overflow-hidden">
            <CardHeader className="bg-white border-b flex flex-row items-center justify-between py-5">
              <CardTitle className="text-xl font-bold">Manage Accounts</CardTitle>
              <div className="relative w-80">
                <Search className="w-4 h-4 absolute left-3 top-3.5 text-slate-400" />
                <Input 
                  placeholder="Filter by name/email..." 
                  className="pl-10 h-11 bg-slate-50 border-none ring-1 ring-slate-200"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader className="bg-slate-50/50">
                  <TableRow>
                    <TableHead className="pl-8 py-4">Identity</TableHead>
                    <TableHead>Balance</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right pr-8">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user._id} className="hover:bg-slate-50/80 transition-all">
                      <TableCell className="pl-8 py-5">
                        <div className="font-bold text-slate-800 flex items-center gap-2">
                          {user.username}
                          {user.role === 'admin' && <Badge variant="outline" className="text-[9px] h-4">ADMIN</Badge>}
                        </div>
                        <div className="text-xs text-slate-400 font-mono">{user.email}</div>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono font-bold text-indigo-600">₹{user.wallet_balance?.toFixed(2)}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.is_suspended ? 'destructive' : 'secondary'} className="uppercase text-[10px] font-bold">
                          {user.is_suspended ? 'Suspended' : 'Verified'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right pr-8 space-x-2">
                        {/* Edit Button */}
                        <Button variant="ghost" size="icon" onClick={() => openEditModal(user)} className="text-slate-500 hover:text-indigo-600">
                          <Edit className="w-4 h-4" />
                        </Button>
                        
                        {/* Top-up Button */}
                        <Button variant="outline" size="sm" onClick={() => setSelectedUserForTopup(user)} className="rounded-lg border-slate-200">
                          <IndianRupee className="w-3 h-3 mr-1" /> Credit
                        </Button>
                        
                        {/* Suspend Button */}
                        <Button
                          variant={user.is_suspended ? "default" : "destructive"}
                          size="sm"
                          onClick={() => toggleSuspend(user)}
                          disabled={user.role === 'admin'}
                          className="rounded-lg w-24"
                        >
                          {user.is_suspended ? 'Unsuspend' : 'Suspend'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 2: PAYMENTS */}
        <TabsContent value="payments">
           <Card className="border-none shadow-xl ring-1 ring-slate-200 overflow-hidden">
            <CardHeader className="bg-white border-b py-5">
              <CardTitle className="text-xl font-bold">Manual Top-up Requests</CardTitle>
              <CardDescription>Review and verify user-uploaded screenshots.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader className="bg-slate-50/50">
                  <TableRow>
                    <TableHead className="pl-8 py-4">Requester</TableHead>
                    <TableHead>Requested Amount</TableHead>
                    <TableHead>Time</TableHead>
                    <TableHead className="text-right pr-8">Verification</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingTxs.length > 0 ? pendingTxs.map((tx) => (
                    <TableRow key={tx.id || tx._id}>
                      <TableCell className="pl-8 py-5">
                        <div className="font-bold text-slate-800">{tx.username || 'User'}</div>
                        <div className="text-[10px] text-slate-400 font-mono">ID: {tx.id || tx._id}</div>
                      </TableCell>
                      <TableCell>
                        <span className="text-emerald-600 font-black text-lg">₹{tx.amount?.toFixed(2)}</span>
                      </TableCell>
                      <TableCell className="text-slate-500 text-xs">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(tx.created_at).toLocaleString()}
                        </div>
                      </TableCell>
                      <TableCell className="text-right pr-8">
                        <Button 
                          onClick={() => setViewingProof(tx)}
                          className="bg-indigo-600 hover:bg-indigo-700 font-bold rounded-xl"
                        >
                          <Eye className="w-4 h-4 mr-2" /> View Proof
                        </Button>
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={4} className="py-24 text-center">
                        <CheckCircle className="w-12 h-12 text-slate-100 mx-auto mb-4" />
                        <p className="text-slate-400 font-medium">All payments verified. No pending requests.</p>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* MODAL 1: EDIT USER DETAILS */}
      <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
        <DialogContent className="rounded-3xl border-none shadow-2xl max-w-2xl">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="text-2xl font-black">Edit User Configuration</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleEditSubmit} className="space-y-6 pt-2 max-h-[70vh] overflow-y-auto pr-2">
            
            {/* SECTION: Account Basics */}
            <div className="space-y-4">
              <h3 className="text-sm font-bold text-indigo-600 uppercase tracking-wider">Account Basics</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Username</label>
                  <Input name="username" value={editForm.username} onChange={handleEditChange} required />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Email Address</label>
                  <Input type="email" name="email" value={editForm.email} onChange={handleEditChange} required />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Role</label>
                  <select 
                    name="role" 
                    value={editForm.role} 
                    onChange={handleEditChange}
                    className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-950"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">AI Auto-Reply</label>
                  <select 
                    name="ai_bot_enabled" 
                    value={editForm.ai_bot_enabled} 
                    onChange={handleEditChange}
                    className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-950"
                  >
                    <option value="true">Enabled</option>
                    <option value="false">Disabled</option>
                  </select>
                </div>
              </div>
            </div>

            {/* SECTION: Custom Pricing */}
            <div className="space-y-4 pt-2 border-t">
              <h3 className="text-sm font-bold text-emerald-600 uppercase tracking-wider">Custom Pricing (INR)</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Text Msg Cost</label>
                  <Input type="number" step="0.001" min="0" name="cost_per_text_msg" value={editForm.cost_per_text_msg} onChange={handleEditChange} required />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Image Msg Cost</label>
                  <Input type="number" step="0.001" min="0" name="cost_per_image_msg" value={editForm.cost_per_image_msg} onChange={handleEditChange} required />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">AI Reply Cost</label>
                  <Input type="number" step="0.001" min="0" name="cost_per_ai_reply" value={editForm.cost_per_ai_reply} onChange={handleEditChange} required />
                </div>
              </div>
            </div>

            {/* SECTION: Resource Limits */}
            <div className="space-y-4 pt-2 border-t">
              <h3 className="text-sm font-bold text-rose-600 uppercase tracking-wider">Resource Limits</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Daily Msgs</label>
                  <Input type="number" min="1" name="max_msgs_per_day" value={editForm.max_msgs_per_day} onChange={handleEditChange} required />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Daily AI Tokens</label>
                  <Input type="number" min="1" name="max_ai_tokens_per_day" value={editForm.max_ai_tokens_per_day} onChange={handleEditChange} required />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Max Image Size (MB)</label>
                  <Input type="number" step="0.1" min="0.1" name="max_image_size_mb" value={editForm.max_image_size_mb} onChange={handleEditChange} required />
                </div>
              </div>
            </div>

            <Button type="submit" disabled={isSaving} className="w-full h-12 mt-6 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-black">
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save Configuration</>}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* MODAL 2: DIRECT TOP-UP */}
      <Dialog open={!!selectedUserForTopup} onOpenChange={() => setSelectedUserForTopup(null)}>
        <DialogContent className="rounded-3xl border-none shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black">Manual Injection: {selectedUserForTopup?.username}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleDirectTopup} className="space-y-4 pt-4">
            <div className="relative">
              <IndianRupee className="absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
              <Input 
                type="number" 
                placeholder="Amount in INR" 
                className="pl-10 h-12 rounded-xl border-slate-200"
                value={topupAmount}
                onChange={(e) => setTopupAmount(e.target.value)} // Fixed variable name mapping here
                required
              />
            </div>
            <Button type="submit" className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 rounded-xl font-black">
              Inject Credit
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* MODAL 3: PROOF VIEWER & APPROVAL */}
      {viewingProof && (
        <Dialog open={!!viewingProof} onOpenChange={() => setViewingProof(null)}>
          <DialogContent className="sm:max-w-[500px] rounded-[2rem] border-none shadow-2xl p-0 overflow-hidden">
            <div className="bg-slate-50 p-6 text-center border-b">
              <h2 className="text-xl font-black text-slate-800 tracking-tight">Verify Payment Proof</h2>
              <p className="text-sm text-slate-500">Amount claimed: <span className="font-bold text-emerald-600">₹{viewingProof.amount}</span></p>
            </div>
            
            <div className="p-6">
              <div className="aspect-[4/5] bg-slate-100 rounded-2xl overflow-hidden shadow-inner border mb-6">
                <img 
                  src={`${process.env.REACT_APP_API_URL || ''}${viewingProof.proof_image}`} 
                  alt="Payment Proof" 
                  className="w-full h-full object-contain"
                  onError={(e) => e.target.src = 'https://placehold.co/400x500?text=Image+Load+Failed'}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Button 
                  onClick={() => handleProcessPayment(viewingProof.id || viewingProof._id, 'approve')}
                  className="bg-emerald-600 hover:bg-emerald-700 h-14 rounded-2xl font-black text-white"
                >
                  <Check className="w-5 h-5 mr-2" /> Approve
                </Button>
                <Button 
                  onClick={() => handleProcessPayment(viewingProof.id || viewingProof._id, 'reject')}
                  variant="outline"
                  className="h-14 rounded-2xl font-black text-rose-600 border-rose-100 hover:bg-rose-50"
                >
                  <X className="w-5 h-5 mr-2" /> Reject
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      
    </div>
  );
}